import { DatePipe } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DigisubservicesService } from '../services/digisubservices.service';
import { of, forkJoin, Observable } from 'rxjs';
import { switchMap, catchError, map, retry } from 'rxjs/operators';
import * as XLSX from 'xlsx';


@Component({
  selector: 'app-trial-balance-new',
  templateUrl: './trial-balance-new.component.html',
  styleUrl: './trial-balance-new.component.scss'
})
export class TrialBalanceNewComponent {

  mainexp: any;
  subexp: any;
  startDate: any;
  endDate: any;
  allvoucherdata: any;
  dr_opening_bal: any;
  currentDate: Date;
  exp_name: any;
  cr_opening_bal: any;
  credittotal: any;
  debittotal: any;
  isFinancialYearStart: boolean = false;
  loading: boolean = false;
  previousPeriodData: any[] = [];

  categories = [
    { name: 'Assent', expenses: [], expanded: true },
    { name: 'Liabilities', expenses: [], expanded: true },
    { name: 'Income', expenses: [], expanded: true },
    { name: 'Expenditure', expenses: [], expanded: true }
  ];
  expenseMap: Map<string, { expenses: any[]; total_final_amt: number; }>;
  expenseArray: any[];

  constructor(
    private datePipe: DatePipe,
    private dialogRef: MatDialogRef<TrialBalanceNewComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private service: DigisubservicesService,
  ) { }

  getFormattedDate(): string {
    return this.datePipe.transform(this.currentDate, 'dd-MM-yyyy');
  }

  toggleCategory(category: any) {
    category.expanded = !category.expanded;
  }

  ngOnInit(): void {
    this.currentDate = new Date();
    this.startDate = this.data['startDate'];
    this.endDate = this.data['endDate'];
    this.mainexp = this.data['mainexp'];
    this.subexp = this.data['subexp'];

    // Strictly check if start date is April 1st, 2024 (fixed financial year start)
    const startDateObj = new Date(this.startDate);
    this.isFinancialYearStart = (startDateObj.getFullYear() === 2024 &&
      startDateObj.getMonth() === 3 &&  // April is month 3 (0-indexed)
      startDateObj.getDate() === 1);

    this.getTrialBalanceReport();

    this.categories.forEach(category => {
      category.expanded = true;
    });
  }

  getTrialBalanceReport() {
    this.loading = true;
    const financialYearStart = '2024-04-01'; // Fixed financial year start date

    if (this.isFinancialYearStart) {
      // If start date is exactly April 1st, 2024, just get the final report
      this.buildEnrichedTrialBalanceData(this.startDate, this.endDate).subscribe(data => {
        this.allvoucherdata = data;
        this.loading = false;
        this.getExpensesWithOrders();
      });
    } else {
      // Any later period: the opening balance is everything since inception
      // (2024-04-01) up to the day before this period starts, and it needs
      // the exact same enrichment (billing/personal-ledger merges - SGST,
      // CGST, IGST, credit notes, outstanding balances, etc.) as the period
      // being displayed. Previously the previous-period fetch only pulled
      // the raw base totals, so any account whose true balance depends on
      // those merges (e.g. SGST - जाहिरात) carried forward wrong.
      const previousEndDate = new Date(this.startDate);
      previousEndDate.setDate(previousEndDate.getDate() - 1);
      const formattedPreviousEndDate = previousEndDate.toISOString().split('T')[0];

      forkJoin({
        previousData: this.buildEnrichedTrialBalanceData(financialYearStart, formattedPreviousEndDate),
        currentData: this.buildEnrichedTrialBalanceData(this.startDate, this.endDate)
      }).subscribe(({ previousData, currentData }) => {
        this.previousPeriodData = (previousData || []).filter(item =>
          item.exp_type === 'Assent' || item.exp_type === 'Liabilities'
        );
        this.allvoucherdata = currentData;
        if (this.previousPeriodData.length > 0) {
          this.mergePreviousPeriodData();
        }
        this.loading = false;
        this.getExpensesWithOrders();
      });
    }
  }

  /**
   * Fetches the trial balance's base data for the given date range, plus all
   * the billing/personal-ledger merges (book, issue seller, advertisement
   * incl. GST, book seller ledger). Used both for the period being displayed
   * and for the "since inception" period used to compute the previous year's
   * closing balance, so both go through identical logic.
   */
  private buildEnrichedTrialBalanceData(startDate: string, endDate: string): Observable<any[]> {
    const cv: any = {
      startdate: startDate,
      enddate: endDate
    };

    return this.service.getTrialBalanceReport(cv).pipe(
      retry(2),
      catchError(err => {
        console.error('Error in getTrialBalanceReport', err);
        return of([]);
      }),
      switchMap((response1: any[]) => {
        let data: any[] = response1 || [];

        return this.service.getdataforbookbilling().pipe(
          retry(2),
          catchError(err => {
            console.error('Error in getdataforbookbilling', err);
            return of([]);
          }),
          map(billingData => {
            const billingForBook = Array.isArray(billingData)
              ? billingData.filter((item: any) => {
                const invoiceDate = new Date(item.invoice_date);
                const start = new Date(startDate);
                const end = new Date(endDate);
                return (
                  parseFloat(item.invoice_total) !== 0 &&
                  invoiceDate >= start &&
                  invoiceDate <= end
                );
              }).map((item: any) => ({
                exp_name: 'पुस्तक व ग्रंथ विक्री खाते',
                sub_exp_name: '',
                final_amt: parseFloat(item.invoice_total),
                exp_type: 'Income'
              }))
              : [];

            data = data.concat(billingForBook);
            return data;
          })
        );
      }),
      switchMap((data: any[]) => {
        const req: any = {
          "start_date": startDate,
          "end_date": endDate,
          "issue_name": '',
          "issue_date": ''
        };
        return this.service.getissueInvoiceRegisterListbriefly(req).pipe(
          retry(2),
          catchError(err => {
            console.error('Error in getissueInvoiceRegisterListbriefly', err);
            return of([]);
          }),
          map(res => {
            const billingForIssueSeller = Array.isArray(res)
              ? res.filter((item: any) => parseFloat(item.beforegst_amt) !== 0)
                .map((item: any) => ({
                  exp_name: 'अंक विक्री',
                  sub_exp_name: '',
                  final_amt: parseFloat(item.beforegst_amt),
                  exp_type: 'Income'
                }))
              : [];
            return data.concat(billingForIssueSeller);
          })
        );
      }),
      switchMap((data: any[]) => {
        const req: any = {
          "start_date": startDate,
          "end_date": endDate,
          "issue_name": '',
          "issue_date": '',
        };
        return this.service.getAdvtInvoiceRegisterListbriefly(req).pipe(
          retry(2),
          catchError(err => {
            console.error('Error in getAdvtInvoiceRegisterListbriefly', err);
            return of([]);
          }),
          map(res => {
            const billingForAdvt = Array.isArray(res)
              ? res.filter((item: any) => parseFloat(item.beforegst_amt) !== 0)
                .map((item: any) => ({
                  exp_name: 'जाहिरात खाते',
                  sub_exp_name: '',
                  final_amt: parseFloat(item.beforegst_amt),
                  exp_type: 'Income'
                }))
              : [];
            const cgst = Array.isArray(res)
              ? res.filter((item: any) => parseFloat(item.cgst) !== 0)
                .map((item: any) => ({
                  exp_name: 'CGST - जाहिरात',
                  sub_exp_name: '',
                  final_amt: parseFloat(item.cgst),
                  exp_type: 'Liabilities'
                }))
              : [];
            const sgst = Array.isArray(res)
              ? res.filter((item: any) => parseFloat(item.sgst) !== 0)
                .map((item: any) => ({
                  exp_name: 'SGST - जाहिरात',
                  sub_exp_name: '',
                  final_amt: parseFloat(item.sgst),
                  exp_type: 'Liabilities'
                }))
              : [];
            const igst = Array.isArray(res)
              ? res.filter((item: any) => parseFloat(item.igst) !== 0)
                .map((item: any) => ({
                  exp_name: 'IGST - जाहिरात',
                  sub_exp_name: '',
                  final_amt: parseFloat(item.igst),
                  exp_type: 'Liabilities'
                }))
              : [];
            return data.concat(billingForAdvt, cgst, sgst, igst);
          }),
        );
      }),
      switchMap((data: any[]) => {
        const cvv = {
          startDate: startDate,
          endDate: endDate,
          issue_seller_name: ''
        };
        return this.service.getIssuePersoanlLedBriefly(cvv).pipe(
          retry(2),
          catchError(err => {
            console.error('Error in getIssuePersoanlLedBriefly', err);
            return of([]);
          }),
          map(res => {
            const mergedData1 = Array.isArray(res)
              ? res.filter((item: any) => parseFloat(item.bal_amt) !== 0)
                .map((item: any) => ({
                  exp_name: 'अंक विक्री / प्रतिनिधि मुख्य खाते',
                  sub_exp_name: item.issue_seller_name,
                  final_amt: (parseFloat(item.bal_amt) * -1),
                  exp_type: 'Assent'
                }))
              : [];

            const mergedData2 = Array.isArray(res)
              ? res.filter((item: any) => parseFloat(item.credit_amt) !== 0)
                .map((item: any) => ({
                  exp_name: 'अंक विक्री क्रेडिट नोट',
                  sub_exp_name: '',
                  final_amt: (parseFloat(item.credit_amt) * -1),
                  exp_type: 'Expenditure'
                }))
              : [];

            return data.concat(mergedData1, mergedData2);
          })
        );
      }),
      switchMap((data: any[]) => {
        const req = {
          start_date: startDate,
          end_date: endDate,
          book_seller_name: ''
        };
        return this.service.getBookSellerLedgerBriefly(req).pipe(
          retry(2),
          catchError(err => {
            console.error('Error in getBookSellerLedgerBriefly', err);
            return of([]);
          }),
          map(res => {
            const mergedData3 = Array.isArray(res)
              ? res.filter((item: any) => parseFloat(item.bal_amt) !== 0)
                .map((item: any) => ({
                  exp_name: 'पुस्तक प्रलंबित देय रक्कम',
                  sub_exp_name: item.book_seller_name,
                  final_amt: (parseFloat(item.bal_amt) * -1),
                  exp_type: 'Assent'
                }))
              : [];

            const mergedData4 = Array.isArray(res)
              ? res.filter((item: any) => parseFloat(item.credit_amt) !== 0)
                .map((item: any) => ({
                  exp_name: 'पुस्तक विक्री क्रेडिट नोट',
                  sub_exp_name: '',
                  final_amt: (parseFloat(item.credit_amt) * -1),
                  exp_type: 'Expenditure'
                }))
              : [];

            return data.concat(mergedData3, mergedData4);
          })
        );
      }),
      switchMap((data: any[]) => {
        const req1: any = {
          "start_date": startDate,
          "end_date": endDate,
          "advt_name": ''
        }
        return this.service.getPersoanlLedBriefly(req1).pipe(
          retry(2),
          catchError(err => {
            console.error('Error in getPersoanlLedBriefly', err);
            return of([]);
          }),
          map(res => {
            const advtCreditNoteAmt = Array.isArray((res as any).data)
              ? (res as any).data
                .filter((item: any) => {
                  const val = parseFloat(item.other_credit_amt);
                  return !isNaN(val) && val !== 0;
                })
                .map((item: any) => ({
                  exp_name: 'जाहिरात क्रेडिट नोट',
                  sub_exp_name: '',
                  final_amt: (parseFloat(item.other_credit_amt) * -1) || 0,
                  exp_type: 'Expenditure'
                }))
              : [];

            const advtOutstandingAmt = Array.isArray((res as any).data)
              ? (res as any).data
                .filter((item: any) => parseFloat(item.balance_amt) !== 0)
                .map((item: any) => ({
                  exp_name: 'जाहिरातदार / देणगीदार खाते',
                  sub_exp_name: item.advt_name,
                  final_amt: (parseFloat(item.balance_amt) * -1),
                  exp_type: 'Assent'
                }))
              : [];

            const advtTdsAmount = Array.isArray((res as any).data)
              ? (res as any).data
                .filter((item: any) => parseFloat(item.tds_amt) !== 0)
                .map((item: any) => ({
                  exp_name: 'जाहिरात टी डी एस',
                  sub_exp_name: '',
                  final_amt: (parseFloat(item.tds_amt) * -1),
                  exp_type: 'Expenditure'
                }))
              : [];

            return data.concat(advtCreditNoteAmt, advtOutstandingAmt, advtTdsAmount);
          })
        );
      })
    );
  }

  // These accounts come from "personal ledger briefly" endpoints whose
  // amount is already the running/closing balance as of end_date (it bakes
  // in everything before start_date internally), not a period-only delta
  // like the voucher-based accounts. Adding the previous-period snapshot on
  // top of the current-period snapshot double-counts everything up to the
  // previous period's end date, so these are excluded from the additive
  // merge below - currentData already holds their correct final balance.
  private readonly SNAPSHOT_BALANCE_EXP_NAMES = new Set<string>([
    'जाहिरातदार / देणगीदार खाते',
    'अंक विक्री / प्रतिनिधि मुख्य खाते',
    'पुस्तक प्रलंबित देय रक्कम'
  ]);

  mergePreviousPeriodData() {
    // Group previous period data by exp_name, sub_exp_name and exp_type.
    // sub_exp_name must be part of the key/match, otherwise every
    // sub-account under the same main exp_name (e.g. each bank under
    // बँक खाते) collapses into one summed bucket and gets dumped onto
    // whichever current-period sub-account is found first.
    const previousDataMap = new Map<string, any>();

    this.previousPeriodData
      .filter(item => !this.SNAPSHOT_BALANCE_EXP_NAMES.has(item.exp_name))
      .forEach(item => {
        const key = `${item.exp_name}|${item.sub_exp_name}|${item.exp_type}`;
        if (previousDataMap.has(key)) {
          previousDataMap.get(key).final_amt += item.final_amt;
        } else {
          previousDataMap.set(key, { ...item });
        }
      });

    // Merge with current data
    previousDataMap.forEach((prevItem, key) => {
      const found = this.allvoucherdata.find((currItem: any) =>
        currItem.exp_name === prevItem.exp_name &&
        currItem.sub_exp_name === prevItem.sub_exp_name &&
        currItem.exp_type === prevItem.exp_type
      );

      if (found) {
        // If exists in current data, add the amounts
        found.final_amt += prevItem.final_amt;
      } else {
        // If doesn't exist, add as new entry
        this.allvoucherdata.push({ ...prevItem });
      }
    });
  }

  expenseOrderList: any[] = [];
  getExpensesWithOrders() {
    this.service.getExpenseOrder().subscribe((res: any) => {
      this.expenseOrderList = res;
      this.processExpenses();
    });
  }

  processExpenses() {

    this.expenseMap = new Map<string, { expenses: any[]; total_final_amt: number }>();

    if (!this.allvoucherdata || this.allvoucherdata.length === 0) {
      this.expenseArray = [];
      return;
    }

    // ⭐ create order map from API
    const orderMap = new Map(
      this.expenseOrderList.map(x => [`${x.category}_${x.name}`, x.expense_order])
    );

    // ⭐ same asset sub-expense order
    const assetSubOrder = [
      'रोख खाते',
      'बँक ऑफ महाराष्ट्र - 20109800154',
      'TJSB सहकारी बँक लि.- 902',
      'ठाणे जनता सह बँक - पुस्तक विभाग',
      'ठाणे जनता सहकारी बँक - ओडी 111',
      'जनता सहकारी बँक लि, CC - 5039',
      'जनता सह बँक लि, - कर्वेरोड शाखा',
      'जनता सह बँक लि - लातूर शाखा',
      'जनता सह बँक लि , औरंगाबाद शाखा',
      'जनकल्याण सहकारी बँक - OD - 1706',
      'बँक ऑफ महाराष्ट्र - रिकरिंग खाते',
      'बँक ऑफ महाराष्ट्र - पर्यटन विभाग',
      'TJSB - शिल्पकार चरित्रकोश',
      'TJSB - EVIVEK.COM',
      'TJSB - संवर्धन निधी खाते',
      'बँक ऑफ महाराष्ट्र - संवाद'
    ];

    this.allvoucherdata
      .filter(item => item.exp_name)
      .forEach(item => {
        const category = this.categories.find(cat => cat.name === item.exp_type);
        if (category) {
          category.expenses.push(item);
        }
      });

    this.categories.forEach(category => {

      // ⭐ grouping (unchanged)
      category.expenses = category.expenses.reduce((acc, current) => {

        const existingGroup = acc.find(group => group.exp_name === current.exp_name);

        const amount = Number(current.final_amt) || 0;

        if (existingGroup) {
          existingGroup.total_final_amt += amount;
          existingGroup.expenses.push(current);
        } else {
          acc.push({
            exp_name: current.exp_name,
            total_final_amt: amount,
            expenses: [current]
          });
        }

        return acc;

      }, []);

      // ⭐ sorting
      category.expenses.sort((a, b) => {

        // same Assent logic as detailed report
        if (category.name === 'Assent') {

          const subIndexA = assetSubOrder.indexOf(a.exp_name);
          const subIndexB = assetSubOrder.indexOf(b.exp_name);

          if (subIndexA !== -1 && subIndexB !== -1) return subIndexA - subIndexB;
          if (subIndexA !== -1) return -1;
          if (subIndexB !== -1) return 1;

        }

        const orderA = orderMap.get(`${category.name}_${a.exp_name}`) ?? 9999;
        const orderB = orderMap.get(`${category.name}_${b.exp_name}`) ?? 9999;

        if (orderA !== orderB) {
          return orderA - orderB;
        }

        return a.exp_name.localeCompare(b.exp_name, 'mr');

      });

    });

  }

  getDebitTotal(): number {
    return this.categories.reduce((total, category) => {
      return total + category.expenses.reduce((sum, group) => {
        if ((group.expenses[0].exp_type === 'Expenditure' || group.expenses[0].exp_type === 'Assent') && group.total_final_amt < 0) {
          return sum + group.total_final_amt;
        }
        if ((group.expenses[0].exp_type === 'Liabilities' || group.expenses[0].exp_type === 'Income') && group.total_final_amt < 0) {
          return sum + group.total_final_amt;
        }
        return sum;
      }, 0);
    }, 0);
  }

  getCreditTotal(): number {
    return this.categories.reduce((total, category) => {
      return total + category.expenses.reduce((sum, group) => {
        if ((group.expenses[0].exp_type === 'Liabilities' || group.expenses[0].exp_type === 'Income') && group.total_final_amt > 0) {
          return sum + group.total_final_amt;
        }
        if ((group.expenses[0].exp_type === 'Expenditure' || group.expenses[0].exp_type === 'Assent') && group.total_final_amt > 0) {
          return sum + group.total_final_amt;
        }
        return sum;
      }, 0);
    }, 0);
  }

  exportExcel(fileName: string): void {
    // helper to compute debit/credit values exactly like your template logic
    const computeRowAmounts = (group: any) => {
      const hasExpenses = group.expenses && group.expenses.length > 0;
      const firstType = hasExpenses ? group.expenses[0].exp_type : null;
      const amt = Number(group.total_final_amt) || 0;

      // Debit logic (show positive number for display)
      const showDebit =
        hasExpenses &&
        (
          ((firstType === 'Expenditure' || firstType === 'Assent') && amt < 0) ||
          ((firstType === 'Liabilities' || firstType === 'Income') && amt < 0)
        );

      // Credit logic
      const showCredit =
        hasExpenses &&
        (
          ((firstType === 'Liabilities' || firstType === 'Income') && amt > 0) ||
          ((firstType === 'Expenditure' || firstType === 'Assent') && amt > 0)
        );

      const debitVal = showDebit ? parseFloat((Math.abs(amt)).toFixed(2)) : '';
      const creditVal = showCredit ? parseFloat((amt).toFixed(2)) : '';

      return { debitVal, creditVal };
    };

    // Build rows as objects with consistent keys (columns)
    const rows: Array<{ 'तपशील': any; 'डेबिट': any; 'क्रेडिट': any }> = [];

    // Header block (matches your HTML header)
    rows.push({ 'तपशील': `दिनांक : ${this.getFormattedDate()}`, 'डेबिट': '', 'क्रेडिट': '' });
    rows.push({ 'तपशील': `पृष्ठ क्र : 1`, 'डेबिट': '', 'क्रेडिट': '' });
    rows.push({ 'तपशील': '', 'डेबिट': '', 'क्रेडिट': '' });

    rows.push({ 'तपशील': `हिंदुस्थान प्रकाशन संस्था`, 'डेबिट': '', 'क्रेडिट': '' });
    rows.push({ 'तपशील': `ऊप खात्यांचा ट्रायल बॅलन्स मुख्य खाते - ${this.exp_name || ''}`, 'डेबिट': '', 'क्रेडिट': '' });
    rows.push({ 'तपशील': `दिनांक पासून : ${this.startDate ? (new Date(this.startDate)).toLocaleDateString('en-GB') : ''}`, 'डेबिट': '', 'क्रेडिट': '' });
    rows.push({ 'तपशील': `दिनांक पर्यंत : ${this.endDate ? (new Date(this.endDate)).toLocaleDateString('en-GB') : ''}`, 'डेबिट': '', 'क्रेडिट': '' });
    rows.push({ 'तपशील': '', 'डेबिट': '', 'क्रेडिट': '' });

    // Table header
    rows.push({ 'तपशील': 'तपशील', 'डेबिट': 'डेबिट', 'क्रेडिट': 'क्रेडिट' });

    // Iterate categories (partitioned report)
    if (Array.isArray(this.categories)) {
      this.categories.forEach((category: any) => {
        // category header row
        rows.push({ 'तपशील': category.name || '---', 'डेबिट': '', 'क्रेडिट': '' });

        if (category.expanded) {
          if (Array.isArray(category.expenses) && category.expenses.length > 0) {
            category.expenses.forEach((group: any) => {
              const { debitVal, creditVal } = computeRowAmounts(group);
              rows.push({
                'तपशील': group.exp_name || '',
                'डेबिट': debitVal === '' ? '' : debitVal,
                'क्रेडिट': creditVal === '' ? '' : creditVal
              });
            });
          } else {
            // Data is loading / no items
            rows.push({
              'तपशील': `Data is Loading... for ${category.name || ''}.`,
              'डेबिट': '',
              'क्रेडिट': ''
            });
          }
        } else {
          // If category collapsed, you may still want an indicator (optional)
          rows.push({ 'तपशील': `(collapsed)`, 'डेबिट': '', 'क्रेडिट': '' });
        }

        // blank line after each category for readability
        rows.push({ 'तपशील': '', 'डेबिट': '', 'क्रेडिट': '' });
      });
    }

    // Final Totals (match your template formatting)
    const debitTotal = typeof this.getDebitTotal === 'function' ? this.getDebitTotal() : 0;
    const creditTotal = typeof this.getCreditTotal === 'function' ? this.getCreditTotal() : 0;

    rows.push({
      'तपशील': 'एकूण :',
      'डेबिट': parseFloat((Math.abs(debitTotal)).toFixed(2)),
      'क्रेडिट': parseFloat((creditTotal).toFixed(2))
    });

    // Generate worksheet & workbook and save
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(rows, { skipHeader: true });

    // Optionally set column widths for readability
    const wscols = [
      { wch: 60 }, // तपशील wide
      { wch: 14 }, // डेबिट
      { wch: 14 }  // क्रेडिट
    ];
    (ws as any)['!cols'] = wscols;

    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, fileName + '.xlsx');
  }

  getDifference(): number {
    return Math.abs(this.getDebitTotal()) - Math.abs(this.getCreditTotal());
  }

}